import React, { useCallback, useEffect, useState } from "react";
import {
  createGroupOrder,
  deleteGroupOrder,
  fetchGroupOrder,
  fetchGroupOrderPrep,
  fetchGroupOrders,
  fetchBuffets,
  fetchMenu,
  fetchMyGroupOrders,
  fetchStaff,
  getCurrency,
  getRole,
  setGroupOrderStatus,
  updateGroupOrder,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

const STATUSES = ["draft", "confirmed", "in_prep", "served", "closed", "cancelled"];
const STATUS_COLOR = {
  draft: "#5f6b78", confirmed: "#1D9E75", in_prep: "#854F0B",
  served: "#2563eb", closed: "#063b48", cancelled: "#A32D2D",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const emptyForm = () => ({
  name: "", organizer: "", contact_person: "", contact_phone: "",
  scheduled_at: "", headcount: "", days: "1", status: "draft", note: "", lines: [], staff_ids: [],
});

export default function GroupOrdersPage() {
  const { t } = useT();
  const role = getRole();
  const isManager = role === "manager";
  return isManager ? <ManagerView t={t} /> : <StaffView t={t} />;
}

// ===================================================================== shared
function useMoney() {
  const cur = getCurrency() || "ETB";
  return (v) => formatPrice(v || 0, cur);
}
const statusLabel = (t, s) => t(`go_status_${s}`) || s;

// ===================================================================== manager
function ManagerView({ t }) {
  const money = useMoney();
  const [list, setList] = useState([]);
  const [menu, setMenu] = useState([]);
  const [staff, setStaff] = useState([]);
  const [buffets, setBuffets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [prep, setPrep] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchGroupOrders("all")
      .then(setList)
      .catch((e) => setErr(e?.response?.data?.detail || t("go_could_not_load")))
      .finally(() => setLoading(false));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    fetchMenu().then(setMenu).catch(() => {});
    fetchStaff().then((s) => setStaff((s || []).filter((x) => x.active !== false && (x.role === "chef" || x.role === "waiter")))).catch(() => {});
    fetchBuffets().then((b) => setBuffets((b || []).filter((x) => x.active !== false))).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(emptyForm()); setPrep(null); setErr(""); setMsg(""); };
  const openEdit = async (id) => {
    setErr(""); setMsg("");
    try {
      const go = await fetchGroupOrder(id);
      setEditing({
        id: go.id, name: go.name || "", organizer: go.organizer || "",
        contact_person: go.contact_person || "", contact_phone: go.contact_phone || "",
        scheduled_at: toLocalInput(go.scheduled_at), headcount: go.headcount ?? "",
        days: go.days ? String(go.days) : "1",
        status: go.status || "draft", note: go.note || "",
        lines: (go.lines || []).map((l) => ({ menu_id: l.menu_id, buffet_package_id: l.buffet_package_id, name: l.name, unit_price: l.unit_price, quantity: l.quantity })),
        staff_ids: go.staff_ids || [],
      });
      fetchGroupOrderPrep(id).then(setPrep).catch(() => setPrep(null));
    } catch (e) { setErr(e?.response?.data?.detail || t("go_could_not_load")); }
  };

  // Open a clean, print-formatted purchase/prep sheet in a new window so
  // the purchasing department can print it (or save as PDF). Uses the
  // already-loaded `prep` data — no extra API call.
  const printPrep = () => {
    if (!prep || !prep.has_recipes) return;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const when = editing.scheduled_at ? new Date(editing.scheduled_at).toLocaleString() : t("go_no_date");
    const rows = prep.ingredients.map((r) =>
      `<tr><td>${esc(r.name)}</td><td class="r"><b>${esc(r.quantity)}</b> ${esc(r.unit)}</td><td class="r">${esc(money(r.cost))}</td></tr>`
    ).join("");
    const buffets = (prep.buffets || []).map((b) =>
      `<li><b>${esc(b.name)}</b> — ${esc(b.headcount)} ${esc(t("go_pax"))} × ${esc(prep.days)}d${b.definition ? ` <span class="muted">(${esc(b.definition)})</span>` : ""}</li>`
    ).join("");
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { setErr(t("go_popup_blocked")); return; }
    w.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(t("go_purchase_list_title"))} — ${esc(editing.name || "")}</title>` +
      `<style>*{font-family:-apple-system,Segoe UI,Roboto,sans-serif;box-sizing:border-box}body{margin:32px;color:#1f2933}` +
      `h1{font-size:20px;margin:0 0 2px;color:#063b48}.sub{color:#5c7c7f;font-size:14px;margin-bottom:16px;font-weight:600}` +
      `.meta{font-size:13px;margin:0 0 14px;line-height:1.7}.meta b{color:#063b48}ul{margin:0 0 16px;padding-left:18px;font-size:13px}` +
      `.muted{color:#7a8a90}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#008080;color:#fff;text-align:left;padding:7px 10px}` +
      `th.r,td.r{text-align:right}td{padding:6px 10px;border-bottom:1px solid #eef1f3}tfoot td{font-weight:700;border-top:2px solid #063b48;border-bottom:none}` +
      `.foot{margin-top:22px;font-size:11px;color:#9aa7ad}.btn{background:#008080;color:#fff;border:none;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer}` +
      `@media print{body{margin:12mm}.noprint{display:none}}</style></head><body>` +
      `<h1>${esc(t("go_purchase_list_title"))}</h1>` +
      `<div class="sub">${esc(editing.name || "—")}</div>` +
      `<div class="meta"><b>${esc(t("go_when"))}:</b> ${esc(when)} &nbsp;·&nbsp; <b>${esc(t("go_pax"))}:</b> ${esc(editing.headcount || "—")} &nbsp;·&nbsp; <b>${esc(t("go_days"))}:</b> ${esc(prep.days)}` +
      `${editing.organizer ? `<br><b>${esc(t("go_organizer"))}:</b> ${esc(editing.organizer)}` : ""}` +
      `${editing.contact_person ? ` &nbsp;·&nbsp; ${esc(editing.contact_person)}` : ""}${editing.contact_phone ? ` · ${esc(editing.contact_phone)}` : ""}</div>` +
      `${buffets ? `<ul>${buffets}</ul>` : ""}` +
      `<table><thead><tr><th>${esc(t("go_ingredient"))}</th><th class="r">${esc(t("go_qty_needed"))}</th><th class="r">${esc(t("go_est_cost"))}</th></tr></thead>` +
      `<tbody>${rows}</tbody>` +
      `<tfoot><tr><td colspan="2" class="r">${esc(t("go_prep_total"))}</td><td class="r">${esc(money(prep.total_cost))}</td></tr></tfoot></table>` +
      `<div class="foot">${esc(t("go_print_footer"))} · ${esc(new Date().toLocaleString())}</div>` +
      `<p class="noprint" style="margin-top:18px"><button class="btn" onclick="window.print()">🖨 ${esc(t("go_print"))}</button></p>` +
      `</body></html>`
    );
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* user can click the button */ } }, 400);
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const toggleStaff = (id) => setEditing((e) => ({
    ...e, staff_ids: e.staff_ids.includes(id) ? e.staff_ids.filter((x) => x !== id) : [...e.staff_ids, id],
  }));
  const addDish = (menuId) => {
    const m = menu.find((x) => String(x.id) === String(menuId));
    if (!m) return;
    setEditing((e) => ({ ...e, lines: [...e.lines, { menu_id: m.id, name: m.name, unit_price: m.price, quantity: 1 }] }));
  };
  const addBuffet = (bid) => {
    const b = buffets.find((x) => String(x.id) === String(bid));
    if (!b) return;
    setEditing((e) => ({ ...e, lines: [...e.lines, { buffet_package_id: b.id, name: b.name, unit_price: b.per_head_price, quantity: Number(e.headcount) || 1 }] }));
  };
  const setLineQty = (i, v) => setEditing((e) => { const ls = [...e.lines]; ls[i] = { ...ls[i], quantity: v }; return { ...e, lines: ls }; });
  const removeLine = (i) => setEditing((e) => ({ ...e, lines: e.lines.filter((_, j) => j !== i) }));

  const days = editing ? Math.max(1, Number(editing.days) || 1) : 1;
  const lineTotal = (l) => (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (l.buffet_package_id ? days : 1);
  const total = editing ? editing.lines.reduce((s, l) => s + lineTotal(l), 0) : 0;

  const save = async () => {
    if (!editing.name.trim()) { setErr(t("go_name_required")); return; }
    setSaving(true); setErr(""); setMsg("");
    try {
      const body = {
        name: editing.name.trim(), organizer: editing.organizer, contact_person: editing.contact_person,
        contact_phone: editing.contact_phone,
        scheduled_at: editing.scheduled_at ? new Date(editing.scheduled_at).toISOString() : null,
        headcount: editing.headcount === "" ? null : Number(editing.headcount),
        status: editing.status, note: editing.note,
        days: Number(editing.days) || 1,
        lines: editing.lines.map((l) => ({ menu_id: l.menu_id, buffet_package_id: l.buffet_package_id, quantity: Number(l.quantity) || 0 })),
        staff_ids: editing.staff_ids,
      };
      const saved = editing.id ? await updateGroupOrder(editing.id, body) : await createGroupOrder(body);
      setMsg(t("go_saved"));
      await openEdit(saved.id);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || t("go_could_not_save")); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing.id) { setEditing(null); return; }
    if (!window.confirm(t("go_confirm_delete"))) return;
    try { await deleteGroupOrder(editing.id); setEditing(null); load(); }
    catch (e) { setErr(e?.response?.data?.detail || t("go_could_not_save")); }
  };

  if (editing) {
    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <h1 style={{ marginBottom: 0 }}>🍽️ {editing.id ? editing.name || t("go_nav") : t("go_new")}</h1>
          <button className="btn secondary small" onClick={() => { setEditing(null); load(); }}>← {t("go_back")}</button>
        </div>
        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

        <div className="card" style={{ marginTop: 12, maxWidth: 760 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 12 }}>
            <Field label={t("go_name")}><input value={editing.name} onChange={(e) => setField("name", e.target.value)} placeholder={t("go_name_ph")} /></Field>
            <Field label={t("go_organizer")}><input value={editing.organizer} onChange={(e) => setField("organizer", e.target.value)} placeholder={t("go_organizer_ph")} /></Field>
            <Field label={t("go_contact")}><input value={editing.contact_person} onChange={(e) => setField("contact_person", e.target.value)} /></Field>
            <Field label={t("go_phone")}><input value={editing.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} /></Field>
            <Field label={t("go_when")}><input type="datetime-local" value={editing.scheduled_at} onChange={(e) => setField("scheduled_at", e.target.value)} /></Field>
            <Field label={t("go_headcount")}><input type="number" min="0" value={editing.headcount} onChange={(e) => setField("headcount", e.target.value)} /></Field>
            <Field label={t("go_days")}><input type="number" min="1" value={editing.days} onChange={(e) => setField("days", e.target.value)} /></Field>
            <Field label={t("go_status")}>
              <select value={editing.status} onChange={(e) => setField("status", e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(t, s)}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("go_note")} style={{ marginTop: 12 }}><textarea rows={2} value={editing.note} onChange={(e) => setField("note", e.target.value)} style={{ width: "100%" }} /></Field>
        </div>

        {/* Assigned staff */}
        <div className="card" style={{ marginTop: 12, maxWidth: 760 }}>
          <h3 style={{ marginTop: 0 }}>👥 {t("go_assign_staff")}</h3>
          {staff.length === 0 ? <p className="muted">{t("go_no_staff")}</p> : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {staff.map((s) => {
                const on = editing.staff_ids.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleStaff(s.id)}
                    className={on ? "btn small" : "btn secondary small"}
                    style={{ borderRadius: 20 }}>
                    {on ? "✓ " : ""}{s.name || s.email} <span style={{ opacity: .7, fontSize: 11 }}>· {s.role}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Dishes */}
        <div className="card" style={{ marginTop: 12, maxWidth: 760 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0 }}>{t("go_dishes")}</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select value="" onChange={(e) => { addDish(e.target.value); e.target.value = ""; }} style={{ maxWidth: 240 }}>
                <option value="">+ {t("go_add_dish")}…</option>
                {menu.map((m) => <option key={m.id} value={m.id}>{m.name} — {money(m.price)}</option>)}
              </select>
              {buffets.length > 0 && (
                <select value="" onChange={(e) => { addBuffet(e.target.value); e.target.value = ""; }} style={{ maxWidth: 240 }}>
                  <option value="">+ {t("go_add_buffet")}…</option>
                  {buffets.map((b) => <option key={b.id} value={b.id}>{b.name} — {money(b.per_head_price)}/pax</option>)}
                </select>
              )}
            </div>
          </div>
          {editing.lines.length === 0 ? <p className="muted" style={{ marginTop: 10 }}>{t("go_no_dishes")}</p> : (
            <div style={{ marginTop: 10 }}>
              {editing.lines.map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid #eef1f3" }}>
                  <span style={{ flex: 1 }}>{l.name}{l.buffet_package_id ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>· {t("go_buffet")} {money(l.unit_price)}/pax × {days}d</span> : null}</span>
                  <input type="number" min="0" step="1" value={l.quantity} onChange={(e) => setLineQty(i, e.target.value)} style={{ width: 80, textAlign: "right" }} />
                  <span className="muted" style={{ width: 90, textAlign: "right", fontSize: 13 }}>{money(lineTotal(l))}</span>
                  <button className="btn secondary small" onClick={() => removeLine(i)} title={t("go_remove")}>✕</button>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10, fontWeight: 700 }}>
                {t("go_total")}: <span style={{ color: "#063b48" }}>{money(total)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Prep & purchase list */}
        <div className="card" style={{ marginTop: 12, maxWidth: 760 }}>
          <div className="space-between" style={{ alignItems: "center" }}>
            <h3 style={{ marginTop: 0 }}>🧾 {t("go_prep_title")}</h3>
            {prep && prep.has_recipes && (
              <button className="btn small secondary" onClick={printPrep}>🖨 {t("go_print")}</button>
            )}
          </div>
          {!editing.id ? <p className="muted">{t("go_prep_save_first")}</p> : (
            <>
              {prep && prep.buffets && prep.buffets.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {prep.buffets.map((b, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid #eef1f3" }}>
                      <b>🍲 {b.name}</b> <span className="muted" style={{ fontSize: 12 }}>· {b.headcount} {t("go_pax")} × {prep.days}d</span>
                      {b.definition && <div className="muted" style={{ fontSize: 13 }}>{b.definition}</div>}
                    </div>
                  ))}
                </div>
              )}
              {!prep || !prep.has_recipes ? <p className="muted">{t("go_prep_hint")}</p> : (
                <>
                  <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "#008080" }}>
                      <th style={{ color: "#fff", textAlign: "left", padding: "6px 10px" }}>{t("go_ingredient")}</th>
                      <th style={{ color: "#fff", textAlign: "right", padding: "6px 10px" }}>{t("go_qty_needed")}</th>
                      <th style={{ color: "#fff", textAlign: "right", padding: "6px 10px" }}>{t("go_est_cost")}</th>
                    </tr></thead>
                    <tbody>
                      {prep.ingredients.map((r, i) => (
                        <tr key={r.ingredient_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                          <td style={{ padding: "5px 10px" }}>{r.name}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right" }}><b>{r.quantity}</b> {r.unit}</td>
                          <td style={{ padding: "5px 10px", textAlign: "right" }}>{money(r.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10, fontWeight: 700 }}>
                    {t("go_prep_total")}: <span style={{ color: "#063b48" }}>{money(prep.total_cost)}</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving}>{saving ? t("go_saving") : t("go_save")}</button>
          <button className="btn secondary" onClick={remove}>{editing.id ? t("go_delete") : t("go_cancel")}</button>
          {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ marginBottom: 0 }}>🍽️ {t("go_nav")}</h1>
        <button className="btn" onClick={openNew}>+ {t("go_new")}</button>
      </div>
      <p className="muted" style={{ marginTop: 4, maxWidth: 680 }}>{t("go_intro")}</p>
      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : list.length === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>{t("go_none")}</p>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 10, maxWidth: 760 }}>
          {list.map((g) => (
            <button key={g.id} className="card" onClick={() => openEdit(g.id)}
              style={{ textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, border: "1px solid var(--border)" }}>
              <span>
                <div style={{ fontWeight: 700 }}>{g.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {g.organizer ? `${g.organizer} · ` : ""}
                  {g.scheduled_at ? new Date(g.scheduled_at).toLocaleString() : t("go_no_date")}
                  {g.headcount ? ` · ${g.headcount} ${t("go_pax")}` : ""}
                  {g.assigned_staff && g.assigned_staff.length ? ` · 👥 ${g.assigned_staff.length}` : ""}
                </div>
              </span>
              <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div style={{ fontWeight: 700, color: "#063b48" }}>{money(g.total)}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: STATUS_COLOR[g.status] || "#5f6b78" }}>● {statusLabel(t, g.status)}</div>
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ====================================================================== staff
function StaffView({ t }) {
  const money = useMoney();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchMyGroupOrders()
      .then(setList)
      .catch((e) => setErr(e?.response?.data?.detail || t("go_could_not_load")))
      .finally(() => setLoading(false));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const advance = async (g, status) => {
    try { await setGroupOrderStatus(g.id, status); load(); }
    catch (e) { setErr(e?.response?.data?.detail || t("go_could_not_save")); }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ marginBottom: 0 }}>🍽️ {t("go_nav")}</h1>
        <button className="btn secondary small" onClick={load}>↻ {t("go_refresh")}</button>
      </div>
      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : list.length === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>{t("go_none_assigned")}</p>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 12, maxWidth: 680 }}>
          {list.map((g) => (
            <div key={g.id} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{g.name}</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {g.organizer ? `${g.organizer} · ` : ""}
                    {g.scheduled_at ? new Date(g.scheduled_at).toLocaleString() : t("go_no_date")}
                    {g.headcount ? ` · ${g.headcount} ${t("go_pax")}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLOR[g.status] || "#5f6b78" }}>● {statusLabel(t, g.status)}</div>
              </div>

              <div style={{ marginTop: 10 }}>
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>{t("go_to_prepare")}</div>
                {(g.lines || []).length === 0 ? <p className="muted" style={{ margin: 0 }}>{t("go_no_dishes")}</p> : (
                  g.lines.map((l) => (
                    <div key={l.id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #f2f4f6" }}>
                      <span>{l.name}</span>
                      <b>× {l.quantity}</b>
                    </div>
                  ))
                )}
              </div>

              {g.note ? <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>📝 {g.note}</p> : null}

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                {(g.status === "draft" || g.status === "confirmed") && (
                  <button className="btn small" onClick={() => advance(g, "in_prep")}>{t("go_start_prep")}</button>
                )}
                {g.status === "in_prep" && (
                  <button className="btn small" onClick={() => advance(g, "served")}>{t("go_mark_served")}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Field({ label, children, style }) {
  return (
    <label style={{ fontSize: 12, color: "#5f6b78", display: "block", ...style }}>
      {label}<br />
      {children}
    </label>
  );
}
